import {
  instantFromIso,
  nowEpochMilliseconds,
} from "@nijiurachan/js/util/time";
import {
  type AppAuthorization,
  decideAppAuthorization,
  inspectAppAuthorization,
  waitForBrowserAuthorization,
} from "@/features/app-auth/api/appAuth";
import {
  createMediator,
  type Mediator,
  type MediatorTransitionResult,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";

export interface AuthorizationServices {
  inspect: typeof inspectAppAuthorization;
  authenticate: typeof waitForBrowserAuthorization;
  decide: typeof decideAppAuthorization;
  navigate: (url: string) => void;
}

export const defaultAuthorizationServices: AuthorizationServices = {
  inspect: inspectAppAuthorization,
  authenticate: waitForBrowserAuthorization,
  decide: decideAppAuthorization,
  navigate: (url: string): void => window.location.assign(url),
};

type Decision = "approve" | "deny" | "cancel";
export interface AuthorizationState {
  authorization: AppAuthorization | null;
  stage: "loading" | "checking" | "ready" | "submitting" | "complete" | "error";
  message: string;
  returnUrl?: string;
}
export type AuthorizationEvent =
  | { type: "authorization.start" }
  | { type: "authorization.retry" }
  | { type: "authorization.inspected"; authorization: AppAuthorization }
  | { type: "authorization.authenticated" }
  | { type: "authorization.decide"; decision: Decision }
  | { type: "authorization.completed"; decision: Decision; returnUrl: string }
  | { type: "authorization.failed"; message: string }
  | { type: "authorization.return" };
type Effect =
  | { type: "inspect" }
  | { type: "authenticate"; expiresAt: string }
  | { type: "decide"; decision: Decision; csrfToken: string }
  | { type: "navigate"; url: string };

export function isAuthorizationEvent(
  event: TypedEvent,
): event is AuthorizationEvent {
  return [
    "authorization.start",
    "authorization.retry",
    "authorization.inspected",
    "authorization.authenticated",
    "authorization.decide",
    "authorization.completed",
    "authorization.failed",
    "authorization.return",
  ].includes(event.type);
}

export function transitionAuthorization(
  state: AuthorizationState,
  event: AuthorizationEvent,
): MediatorTransitionResult<AuthorizationState, Effect> {
  switch (event.type) {
    case "authorization.start":
    case "authorization.retry":
      return {
        status: "handled",
        state: { stage: "loading", authorization: null, message: "" },
        effects: [{ type: "inspect" }],
      };
    case "authorization.inspected":
      return {
        status: "handled",
        state: {
          ...state,
          stage: "checking",
          authorization: event.authorization,
        },
        effects: [
          { type: "authenticate", expiresAt: event.authorization.expiresAt },
        ],
      };
    case "authorization.authenticated":
      return state.stage === "checking"
        ? { status: "handled", state: { ...state, stage: "ready" } }
        : { status: "handled", state };
    case "authorization.decide":
      if (
        !state.authorization ||
        state.stage === "submitting" ||
        state.stage === "complete" ||
        (event.decision === "approve" && state.stage !== "ready")
      )
        return { status: "handled", state };
      return {
        status: "handled",
        state: { ...state, stage: "submitting" },
        effects: [
          {
            type: "decide",
            decision: event.decision,
            csrfToken: state.authorization.csrfToken,
          },
        ],
      };
    case "authorization.completed":
      return {
        status: "handled",
        state: {
          ...state,
          stage: "complete",
          returnUrl: event.returnUrl,
          message:
            event.decision === "approve"
              ? "許可しました。アプリへ戻って認証を完了してください。"
              : "認証を取り消しました。アプリへ戻れます。",
        },
        effects: [{ type: "navigate", url: event.returnUrl }],
      };
    case "authorization.failed":
      return {
        status: "handled",
        state: { ...state, stage: "error", message: event.message },
      };
    case "authorization.return":
      return {
        status: "handled",
        state,
        effects:
          state.stage === "complete" && state.returnUrl
            ? [{ type: "navigate", url: state.returnUrl }]
            : [],
      };
  }
}

export function createAuthorizationMediator(
  requestId: string,
  services = defaultAuthorizationServices,
): Mediator<AuthorizationState, AuthorizationEvent, Effect> {
  let active: AbortController | null = null;
  let browserToken: string | undefined;
  const mediator = createMediator<
    AuthorizationState,
    AuthorizationEvent,
    Effect
  >(
    { authorization: null, stage: "loading", message: "" },
    transitionAuthorization,
    {
      runEffect(effect: Effect): void {
        if (effect.type === "navigate") {
          try {
            services.navigate(effect.url);
          } catch (error) {
            mediator.dispatch({
              type: "authorization.failed",
              message:
                error instanceof Error
                  ? error.message
                  : "通信に失敗しました。アプリから新しい認証を開始してください。",
            });
          }
          return;
        }
        active?.abort();
        const request = new AbortController();
        active = request;
        if (effect.type === "inspect") browserToken = undefined;
        void (async (): Promise<void> => {
          try {
            switch (effect.type) {
              case "inspect": {
                if (!/^[0-9a-f-]{36}$/i.test(requestId))
                  throw new Error(
                    "認証要求が見つかりません。アプリから認証を開始してください。",
                  );
                const authorization = await services.inspect(
                  requestId,
                  request.signal,
                );
                if (!request.signal.aborted)
                  mediator.dispatch({
                    type: "authorization.inspected",
                    authorization,
                  });
                break;
              }
              case "authenticate": {
                const token = await services.authenticate(request.signal);
                if (request.signal.aborted) return;
                const expiresAt = instantFromIso(effect.expiresAt);
                if (
                  expiresAt === null ||
                  expiresAt.epochMilliseconds <= nowEpochMilliseconds()
                )
                  throw new Error(
                    "認証要求の有効期限が切れました。アプリからやり直してください。",
                  );
                browserToken = token;
                mediator.dispatch({ type: "authorization.authenticated" });
                break;
              }
              case "decide": {
                const result = await services.decide(
                  requestId,
                  effect.csrfToken,
                  effect.decision,
                  effect.decision === "approve" ? browserToken : undefined,
                  request.signal,
                );
                if (!request.signal.aborted)
                  mediator.dispatch({
                    type: "authorization.completed",
                    decision: effect.decision,
                    returnUrl: result.redirectUrl,
                  });
                break;
              }
            }
          } catch (error) {
            if (!request.signal.aborted)
              mediator.dispatch({
                type: "authorization.failed",
                message:
                  error instanceof Error
                    ? error.message
                    : effect.type === "decide"
                      ? "通信に失敗しました。アプリから新しい認証を開始してください。"
                      : "認証を完了できませんでした。",
              });
          }
        })();
      },
    },
  );
  return {
    ...mediator,
    get isDisposed(): boolean {
      return mediator.isDisposed;
    },
    dispose(): void {
      active?.abort();
      browserToken = undefined;
      mediator.dispose();
    },
  };
}
